import {
  createStartDateWithTime,
  createStartOfDay,
  createEndDateWithTime,
  createEndOfDay,
  getBrowserTimezone,
} from '../../../api/tickets/queries/dateRangeHelpers';

/**
 * Creates the start boundary timestamp for date range filtering
 * @param {Object} dateRange - Date range object with startDate, startTime
 * @param {string} timezone - IANA timezone identifier
 * @returns {Date|null} Start boundary date or null if no startDate
 */
const createStartBoundary = (dateRange, timezone) => {
  if (!dateRange.startDate) {
    return null;
  }

  const startDate = new Date(dateRange.startDate);

  if (dateRange.startTime) {
    return createStartDateWithTime(startDate, dateRange.startTime, timezone);
  }

  return createStartOfDay(startDate, timezone);
};

/**
 * Creates the end boundary timestamp for date range filtering
 * @param {Object} dateRange - Date range object with endDate, endTime
 * @param {string} timezone - IANA timezone identifier
 * @returns {Date|null} End boundary date or null if no endDate
 */
const createEndBoundary = (dateRange, timezone) => {
  if (!dateRange.endDate) {
    return null;
  }

  const endDate = new Date(dateRange.endDate);

  if (dateRange.endTime) {
    return createEndDateWithTime(endDate, dateRange.endTime, timezone);
  }

  return createEndOfDay(endDate, timezone);
};

/**
 * Checks if conversation created date is after start boundary
 * @param {Object} conversation - Conversation object with createdAt timestamp
 * @param {Date|null} startBoundary - Start boundary date
 * @returns {boolean} True if within boundary or no boundary set
 */
const isAfterStartBoundary = (conversation, startBoundary) => {
  if (!startBoundary) {
    return true;
  }

  return conversation.createdAt >= startBoundary;
};

/**
 * Checks if conversation updated date is before end boundary
 * @param {Object} conversation - Conversation object with updatedAt timestamp
 * @param {Date|null} endBoundary - End boundary date
 * @returns {boolean} True if within boundary or no boundary set
 */
const isBeforeEndBoundary = (conversation, endBoundary) => {
  if (!endBoundary) {
    return true;
  }

  return conversation.updatedAt <= endBoundary;
};

/**
 * Checks if a conversation is within the specified date range
 * NOTE(vlad|SPD-14091:2026-02-05): Used for live query filtering since LiveQuery doesn't respect date filters
 * NOTE(vlad|SPD-14824|2026-05-12): Always uses browser TZ — must match buildDateRangeFilter so the
 * server-side query and client-side live filter agree.
 * @param {Object} conversation - Conversation object with createdAt and updatedAt timestamps
 * @param {Object} dateRange - Date range object with startDate, endDate, startTime, endTime
 * @returns {boolean} True if conversation is within range, false otherwise
 */
export const isConversationInDateRange = (conversation, dateRange) => {
  if (!dateRange) {
    return true;
  }

  const timezone = getBrowserTimezone();
  const startBoundary = createStartBoundary(dateRange, timezone);
  const endBoundary = createEndBoundary(dateRange, timezone);

  return isAfterStartBoundary(conversation, startBoundary) && isBeforeEndBoundary(conversation, endBoundary);
};
